/**
 * ModulesView.js - Zone d'affichage des modules avec refresh automatique
 */

import { createElement } from "../../utils/dom.js";
import { loadCurrentProject, getModuleStats } from "../../utils/storage.js";

export class ModulesView {
  constructor(parentElement, options = {}) {
    if (!parentElement) {
      throw new Error("ModulesView: parentElement est requis");
    }

    this.parentElement = parentElement;
    this.options = {
      project: options.project || null,
      currentTiroir: options.currentTiroir || 1,
      onModuleEdit: options.onModuleEdit || (() => {}),
      onTiroirToggle: options.onTiroirToggle || (() => {}),
      onModuleToggle: options.onModuleToggle || (() => {}),
      ...options,
    };

    this.container = null;
    this.moduleLetters = this.getModuleLetters();

    this.render();
    this.setupDataChangeListener();
  }

  setupDataChangeListener() {
    window.addEventListener("dataChanged", (event) => {
      const detail = event.detail;

      // Si c'est un changement de module du tiroir actuel
      if (detail.source === "moduleToggle" && detail.tiroirNum === this.options.currentTiroir) {
        // Rafraîchir les stats du module concerné
        this.updateModuleStats(detail.moduleLetter);

        // Notifier le parent pour mise à jour du % du tiroir
        this.notifyTiroirStatsChange();
      }

      // : Si c'est un changement de module d'un autre tiroir
      if (detail.source === "moduleToggle" && detail.tiroirNum !== this.options.currentTiroir) {
        // Calculer les stats du tiroir modifié même s'il n'est pas actuel
        this.notifyOtherTiroirStatsChange(detail.tiroirNum);
      }

      // Si c'est un changement général de données
      if (detail.source === "module" || detail.source === "moduleData") {
        this.refresh();
      }
    });

    // : Écouter les demandes de calcul de stats
    window.addEventListener("requestTiroirStats", (event) => {
      const { tiroirNum } = event.detail;

      // Calculer et notifier immédiatement
      setTimeout(() => {
        this.notifyOtherTiroirStatsChange(tiroirNum);
      }, 50); // Petit délai pour s'assurer que les données sont à jour
    });
  }

  /**
   * ✅ NOUVEAU : Notifier le changement des stats d'un autre tiroir
   */
  notifyOtherTiroirStatsChange(tiroirNum) {
    // Calculer les stats du tiroir spécifié
    const globalStats = this.calculateSpecificTiroirStats(tiroirNum);

    // Déclencher un événement pour mettre à jour la navigation
    window.dispatchEvent(
      new CustomEvent("tiroirStatsChanged", {
        detail: {
          tiroirNum: tiroirNum,
          stats: globalStats,
        },
      })
    );
  }

  /**
   * ✅ NOUVEAU : Calculer les stats d'un tiroir spécifique
   */
  calculateSpecificTiroirStats(tiroirNum) {
    if (!this.options.project) return { configured: 0, total: 0, percentage: 0 };

    let totalConfigured = 0;
    let totalFibers = 0;

    // Obtenir les lettres de modules selon le type de fibre
    const moduleLetters =
      this.options.project.fiberType === 24
        ? ["A", "B", "C", "D", "E", "F"]
        : ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

    moduleLetters.forEach((letter) => {
      // Vérifier si le module est actif pour ce tiroir
      const tiroirKey = `T${tiroirNum}`;
      const activeModules = this.options.project?.activeModules?.[tiroirKey] || [];

      if (activeModules.includes(letter)) {
        // Charger les stats de ce module spécifique
        const project = loadCurrentProject();
        const moduleKey = `T${tiroirNum}-${letter}`;
        const moduleData = project?.fiberData?.[moduleKey];

        if (moduleData && moduleData.statuses) {
          const configured = moduleData.statuses.filter((s) => s !== "N/A").length;
          totalConfigured += configured;
        }
        totalFibers += this.options.project?.fiberType || 24;
      }
    });

    const percentage = totalFibers > 0 ? Math.round((totalConfigured / totalFibers) * 100) : 0;

    return {
      configured: totalConfigured,
      total: totalFibers,
      percentage: percentage,
    };
  }

  /**
   * ✅ NOUVEAU : Notifier le changement des stats du tiroir
   */
  notifyTiroirStatsChange() {
    // Calculer les nouvelles stats globales du tiroir
    const globalStats = this.calculateTiroirGlobalStats();

    // Déclencher un événement pour mettre à jour la navigation
    window.dispatchEvent(
      new CustomEvent("tiroirStatsChanged", {
        detail: {
          tiroirNum: this.options.currentTiroir,
          stats: globalStats,
        },
      })
    );
  }

  /**
   * ✅ NOUVEAU : Calculer les stats globales du tiroir
   */
  calculateTiroirGlobalStats() {
    let totalConfigured = 0;
    let totalFibers = 0;

    this.moduleLetters.forEach((letter) => {
      if (this.isModuleActive(letter)) {
        const stats = this.getModuleStats(letter);
        totalConfigured += stats.configured;
        totalFibers += stats.total;
      }
    });

    const percentage = totalFibers > 0 ? Math.round((totalConfigured / totalFibers) * 100) : 0;

    return {
      configured: totalConfigured,
      total: totalFibers,
      percentage: percentage,
    };
  }

  /**
   * Créer le container du composant
   */
  createContainer() {
    this.container = createElement("div", {
      className: "modules-view",
    });

    this.parentElement.appendChild(this.container);
    return this.container;
  }

  /**
   * Obtenir les lettres des modules selon le type de fibre
   */
  getModuleLetters() {
    if (!this.options.project) return [];

    const fiberType = this.options.project.fiberType;
    if (fiberType === 24) {
      return ["A", "B", "C", "D", "E", "F"];
    } else {
      return ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
    }
  }

  /**
   * Vérifier si un module est actif
   */
  isModuleActive(moduleLetter) {
    const tiroirKey = `T${this.options.currentTiroir}`;
    const activeModules = this.options.project?.activeModules?.[tiroirKey] || [];
    return activeModules.includes(moduleLetter);
  }

  /**
   * Obtenir les statistiques d'un module
   */
  getModuleStats(moduleLetter) {
    const project = loadCurrentProject();
    if (!project?.data) {
      return { configured: 0, libres: 0, occupees: 0, total: this.options.project?.fiberType || 24 };
    }

    return getModuleStats(
      project.data,
      `T${this.options.currentTiroir}`,
      moduleLetter,
      this.options.project?.fiberType || 24
    );
  }

  /**
   * Générer le HTML d'une card module
   */
  renderModuleCard(moduleLetter) {
    const isActive = this.isModuleActive(moduleLetter);
    const stats = this.getModuleStats(moduleLetter);
    const fiberType = this.options.project?.fiberType || 24;

    const cardClass = isActive ? "module-card" : "module-card module-card-disabled";

    return `
      <div class="${cardClass}" data-module="${moduleLetter}">
        <div class="module-card-header">
          <div class="module-info">
            <h3 class="module-title">MODULE ${moduleLetter}</h3>
            <p class="module-subtitle">${fiberType} fibres</p>
          </div>
          <div class="module-controls">
            <button 
              class="module-edit-btn ${isActive ? "" : "module-edit-btn-disabled"}"
              data-module="${moduleLetter}"
              ${!isActive ? "disabled" : ""}
            >
              ✏️
            </button>
            <button class="power-switch ${
              isActive ? "power-switch-on" : "power-switch-off"
            }" data-module="${moduleLetter}">
              <div class="power-icon"></div>
            </button>
          </div>
        </div>
        
        <div class="module-stats">
          <div class="stat-item">
            <span class="stat-label">Configurées:</span>
            <span class="stat-value">${stats.configured}/${stats.total}</span>
          </div>
          <div class="stat-row">
            <div class="stat-item stat-libre">
              <span class="stat-dot"></span>
              <span class="stat-count">${stats.libres}</span>
              <span class="stat-text">Libres</span>
            </div>
            <div class="stat-item stat-occupee">
              <span class="stat-dot"></span>
              <span class="stat-count">${stats.occupees}</span>
              <span class="stat-text">Occupées</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Mettre à jour le switch d'un module
   */
  updateModuleSwitch(moduleLetter) {
    const moduleCard = this.container.querySelector(`[data-module="${moduleLetter}"]`);
    const isActive = this.isModuleActive(moduleLetter);

    if (moduleCard) {
      const powerSwitch = moduleCard.querySelector(".power-switch");
      const editBtn = moduleCard.querySelector(".module-edit-btn");

      // Mettre à jour la card
      moduleCard.className = isActive ? "module-card" : "module-card module-card-disabled";

      // Mettre à jour le switch
      if (powerSwitch) {
        powerSwitch.className = `power-switch ${isActive ? "power-switch-on" : "power-switch-off"}`;
      }

      // Mettre à jour le bouton d'édition
      if (editBtn) {
        editBtn.className = `module-edit-btn ${isActive ? "" : "module-edit-btn-disabled"}`;
        editBtn.disabled = !isActive;
      }
    }
  }

  /**
   * ✅ AMÉLIORÉ : Mettre à jour les statistiques d'un module
   */
  updateModuleStats(moduleLetter) {
    const moduleCard = this.container.querySelector(`[data-module="${moduleLetter}"]`);
    if (!moduleCard) return;

    const stats = this.getModuleStats(moduleLetter);

    // Mettre à jour les valeurs
    const configuredEl = moduleCard.querySelector(".stat-value");
    const libresEl = moduleCard.querySelector(".stat-libre .stat-count");
    const occupeesEl = moduleCard.querySelector(".stat-occupee .stat-count");

    if (configuredEl) configuredEl.textContent = `${stats.configured}/${stats.total}`;
    if (libresEl) libresEl.textContent = stats.libres;
    if (occupeesEl) occupeesEl.textContent = stats.occupees;
  }

  /**
   * Changer de tiroir
   */
  setCurrentTiroir(tiroirNum) {
    this.options.currentTiroir = tiroirNum;
    this.render();

    // : Rafraîchir les stats du nouveau tiroir
    setTimeout(() => {
      this.notifyTiroirStatsChange();
    }, 100);
  }

  /**
   * Mettre à jour le projet
   */
  updateProject(newProject) {
    this.options.project = newProject;
    this.moduleLetters = this.getModuleLetters();
    this.render();

    // : Rafraîchir les stats de tous les tiroirs actifs
    setTimeout(() => {
      if (newProject && newProject.activeTiroirs) {
        newProject.activeTiroirs.forEach((tiroirNum) => {
          this.notifyOtherTiroirStatsChange(tiroirNum);
        });
      }
    }, 200);
  }

  /**
   * Rendu de la vue des modules
   */
  render() {
    if (!this.container) {
      this.createContainer();
    }

    if (!this.options.project) {
      this.container.innerHTML = `
        <div class="modules-empty">
          <p>Aucun projet chargé</p>
        </div>
      `;
      return;
    }

    this.container.innerHTML = `
      <div class="modules-container">
        <!-- Grille des modules -->
        <div class="modules-grid">
          ${this.moduleLetters.map((letter) => this.renderModuleCard(letter)).join("")}
        </div>
      </div>
    `;

    this.setupEventListeners();
  }

  /**
   * Configurer les événements
   */
  setupEventListeners() {
    // Boutons d'édition des modules
    const editButtons = this.container.querySelectorAll(".module-edit-btn:not([disabled])");
    editButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const moduleLetter = btn.dataset.module;
        const moduleTitle = `TIROIR : T${this.options.currentTiroir} - MODULE ${moduleLetter}`;

        this.options.onModuleEdit(moduleTitle, moduleLetter);
      });
    });

    // Switches des modules
    const moduleSwitches = this.container.querySelectorAll(".power-switch");
    moduleSwitches.forEach((switchBtn) => {
      switchBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const moduleLetter = switchBtn.dataset.module;
        const newState = !this.isModuleActive(moduleLetter);

        this.options.onModuleToggle(this.options.currentTiroir, moduleLetter, newState);
      });
    });
  }

  /**
   * Animer l'apparition des modules
   */
  animate() {
    const moduleCards = this.container.querySelectorAll(".module-card");
    moduleCards.forEach((card, index) => {
      setTimeout(() => {
        card.style.opacity = "0";
        card.style.transform = "translateY(20px)";
        card.style.transition = "all 0.5s ease-out";

        requestAnimationFrame(() => {
          card.style.opacity = "1";
          card.style.transform = "translateY(0)";
        });
      }, index * 100);
    });
  }

  /**
   * Rafraîchir l'affichage
   */
  refresh() {
    this.moduleLetters.forEach((letter) => {
      this.updateModuleStats(letter);
      this.updateModuleSwitch(letter);
    });

    // : Notifier le changement des stats du tiroir
    this.notifyTiroirStatsChange();
  }

  /**
   * Obtenir l'élément DOM du composant
   */
  getElement() {
    return this.container;
  }

  /**
   * Vérifier si le composant est monté
   */
  isMounted() {
    return this.container && this.container.parentNode === this.parentElement;
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // : Supprimer les écouteurs d'événements
    window.removeEventListener("dataChanged", this.setupDataChangeListener);
    window.removeEventListener("requestTiroirStats", this.setupDataChangeListener);

    // Supprimer les événements
    const buttons = this.container?.querySelectorAll("button");
    buttons?.forEach((btn) => {
      btn.removeEventListener("click", btn.onclick);
    });

    // Démonter le composant
    this.unmount();

    // Nettoyer les références
    this.container = null;
    this.parentElement = null;
  }
}
